import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import styles from './BackofficeLayout.module.scss';
import styled from 'styled-components';
import BotoneraModern from '../Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import BackofficeHeader from './components/Shared/BackofficeHeader.tsx';
import TabNavigation from './components/Shared/TabNavigation.tsx';
import IdentityValidationSection from './components/IdentityValidation/IdentityValidationSection.tsx';
import AnteProyectosValidationSection from './components/AnteProyectosValidation/AnteProyectosValidationSection.tsx';
import AdminDashboardSection from './components/AdminDashboard/AdminDashboardSection.tsx';
import { fetchIdentityPhotos, validatedIdentityWorker } from '../../store/actions/registerActions';
import {
  setActiveTab,
  fetchPendingAnteProyectos,
  fetchBackofficeStats,
  setBackofficeFilters,
  setBackofficePage,
  validateAnteProyecto
} from '../../store/actions/backofficeActions.ts';
import { fetchAdminDashboardStats } from '../../store/actions/dashboardActions';
import { BackofficeFilters } from '../../types/backoffice.ts';

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding-right: 5%;
  padding-left: 5%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 1200px) {
    padding-right: 5%;
    padding-left: 5%;
  }

  @media (max-width: 768px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

interface BackofficeLayoutProps {
  fetchIdentityPhotos: () => void;
  workBagUsersToValidate: any[];
  validatedIdentityWorker: (userId: string, isValid: boolean) => void;
  activeTab: 'identity' | 'anteproyectos' | 'dashboard';
  pendingAnteProyectos: any[];
  stats: any;
  statsLoading: boolean;
  anteProyectosLoading: boolean;
  pagination: any;
  filters: BackofficeFilters;
  validationLoading: boolean;
  dashboardStats: any;
  dashboardStatsLoading: boolean;
  setActiveTab: (tab: 'identity' | 'anteproyectos' | 'dashboard') => void;
  fetchPendingAnteProyectos: (page: number, filters?: Partial<BackofficeFilters>) => void;
  fetchBackofficeStats: () => void;
  setBackofficeFilters: (filters: Partial<BackofficeFilters>) => void;
  setBackofficePage: (page: number) => void;
  validateAnteProyecto: (validation: any) => Promise<any>;
  fetchAdminDashboardStats: () => void;
}

const getAdminDashboardStats = (dashboard: any) => {
  const totalUsers = Array.isArray(dashboard?.totalUsers) ? dashboard.totalUsers.length : (dashboard?.totalUsers ?? 0);
  const totalPublicProjects = typeof dashboard?.totalPublicProjects === 'number' ? dashboard.totalPublicProjects : (Array.isArray(dashboard?.totalPublicProjects) ? dashboard.totalPublicProjects.length : 0);
  const totalPrivateProjects = Array.isArray(dashboard?.totalPrivateProjects) ? dashboard.totalPrivateProjects.length : (dashboard?.totalPrivateProjects ?? 0);
  const totalPublicQuotations = typeof dashboard?.totalPublicQuotations === 'number' ? dashboard.totalPublicQuotations : (Array.isArray(dashboard?.totalPublicQuotations) ? dashboard.totalPublicQuotations.length : 0);
  const totalPrivateQuotations = Array.isArray(dashboard?.totalPrivateQuotations) ? dashboard.totalPrivateQuotations.length : (dashboard?.totalPrivateQuotations ?? 0);
  return {
    totalUsers,
    totalPublicProjects,
    totalPrivateProjects,
    totalPublicQuotations,
    totalPrivateQuotations
  };
};

const BackofficeLayout: React.FC<BackofficeLayoutProps> = ({
  fetchIdentityPhotos,
  workBagUsersToValidate,
  validatedIdentityWorker,
  activeTab,
  pendingAnteProyectos,
  stats,
  statsLoading,
  anteProyectosLoading,
  pagination,
  filters,
  validationLoading,
  dashboardStats,
  dashboardStatsLoading,
  setActiveTab,
  fetchPendingAnteProyectos,
  fetchBackofficeStats,
  setBackofficeFilters,
  setBackofficePage,
  validateAnteProyecto,
  fetchAdminDashboardStats
}) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();

  useEffect(() => {
    fetchIdentityPhotos();
    fetchBackofficeStats();
  }, [fetchIdentityPhotos, fetchBackofficeStats]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAdminDashboardStats();
    }
  }, [activeTab, fetchAdminDashboardStats]);

  useEffect(() => {
    if (activeTab === 'anteproyectos') {
      fetchPendingAnteProyectos(pagination.currentPage, filters);
    }
  }, [activeTab, fetchPendingAnteProyectos, pagination.currentPage, filters]);

  const handleRefresh = useCallback(() => {
    if (activeTab === 'identity') {
      fetchIdentityPhotos();
    } else if (activeTab === 'anteproyectos') {
      fetchPendingAnteProyectos(pagination.currentPage, filters);
    } else if (activeTab === 'dashboard') {
      fetchAdminDashboardStats();
    }
    fetchBackofficeStats();
  }, [activeTab, fetchIdentityPhotos, fetchPendingAnteProyectos, pagination.currentPage, filters, fetchBackofficeStats, fetchAdminDashboardStats]);

  const handleIdentityValidation = (userId: string, isValid: boolean) => {
    validatedIdentityWorker(userId, isValid);
    setTimeout(() => {
      fetchIdentityPhotos();
      fetchBackofficeStats();
    }, 500);
  };

  const handleAnteProyectoValidation = async (
    id: string,
    isApproved: boolean,
    reason?: string,
    adminNote?: string
  ) => {
    const result = await validateAnteProyecto({ id, isApproved, reason, adminNote });
    if (result.success) {
      setTimeout(() => {
        fetchPendingAnteProyectos(pagination.currentPage, filters);
        fetchBackofficeStats();
      }, 500);
    }
  };

  const handleTabChange = (tab: 'identity' | 'anteproyectos' | 'dashboard') => {
    setActiveTab(tab);
  };

  return (
    <Container>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      
      <div className={styles.backofficeLayout}>
        <BackofficeHeader 
          stats={stats} 
          loading={statsLoading}
          onRefresh={handleRefresh}
        />
        
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          identityCount={stats.totalPendingIdentities}
          projectsCount={stats.totalPendingAnteProyectos}
        />

        <div className={styles.content}>
          {activeTab === 'dashboard' && (
            <AdminDashboardSection
              stats={getAdminDashboardStats(dashboardStats)}
              loading={dashboardStatsLoading}
            />
          )}

          {activeTab === 'identity' && (
            <IdentityValidationSection
              users={workBagUsersToValidate || []}
              loading={false}
              onValidate={handleIdentityValidation}
            />
          )}

          {activeTab === 'anteproyectos' && (
            <AnteProyectosValidationSection
              anteProyectos={pendingAnteProyectos}
              loading={anteProyectosLoading}
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              filters={filters}
              onPageChange={setBackofficePage}
              onFilterChange={setBackofficeFilters}
              onValidate={handleAnteProyectoValidation}
              validationLoading={validationLoading}
            />
          )}
        </div>
      </div>
    </Container>
  );
};

const mapStateToProps = (state: any) => ({
  workBagUsersToValidate: state.register.workBagUsersToValidate,
  activeTab: state.backoffice.activeTab,
  pendingAnteProyectos: state.backoffice.pendingAnteProyectos,
  stats: state.backoffice.stats,
  statsLoading: state.backoffice.statsLoading,
  anteProyectosLoading: state.backoffice.anteProyectosLoading,
  pagination: state.backoffice.pagination,
  filters: state.backoffice.filters,
  validationLoading: state.backoffice.validationLoading,
  dashboardStats: state.dashboard,
  dashboardStatsLoading: false
});

export default connect(mapStateToProps, {
  fetchIdentityPhotos,
  validatedIdentityWorker,
  setActiveTab,
  fetchPendingAnteProyectos,
  fetchBackofficeStats,
  setBackofficeFilters,
  setBackofficePage,
  validateAnteProyecto,
  fetchAdminDashboardStats
})(BackofficeLayout);

